import { useState, useEffect, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { schoolYearsApi, SchoolYear } from '@/api/school-years';
import { DataTable, Column } from '@/components/ui/data-table';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Plus, Pencil, Trash2, CalendarDays } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';

export function SchoolYearsPage() {
  const navigate = useNavigate();
  const { hasRole } = useAuth();
  const [schoolYears, setSchoolYears] = useState<SchoolYear[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchValue, setSearchValue] = useState('');
  const [sortBy, setSortBy] = useState<string>('startingDate');
  const [sortOrder, setSortOrder] = useState<'ASC' | 'DESC'>('DESC');
  const [deleteSchoolYear, setDeleteSchoolYear] = useState<SchoolYear | null>(null);
  const [deleting, setDeleting] = useState(false);

  const isAdmin = hasRole('ROLE_ADMIN') || hasRole('ROLE_SUPERADMIN');

  useEffect(() => {
    fetchSchoolYears();
  }, []);

  const fetchSchoolYears = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await schoolYearsApi.getAll();
      setSchoolYears(data);
    } catch (err) {
      setError('Erreur lors du chargement des annees scolaires');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleSort = (key: string) => {
    if (sortBy === key) {
      setSortOrder(sortOrder === 'ASC' ? 'DESC' : 'ASC');
    } else {
      setSortBy(key);
      setSortOrder('ASC');
    }
  };

  const handleDelete = async () => {
    if (!deleteSchoolYear) return;

    try {
      setDeleting(true);
      await schoolYearsApi.delete(deleteSchoolYear.id);
      setSchoolYears(schoolYears.filter((sy) => sy.id !== deleteSchoolYear.id));
      setDeleteSchoolYear(null);
    } catch (err) {
      console.error(err);
      setError('Erreur lors de la suppression de l\'année fiscale');
    } finally {
      setDeleting(false);
    }
  };

  const filteredAndSortedSchoolYears = useMemo(() => {
    let result = [...schoolYears];

    // Filter
    if (searchValue) {
      const search = searchValue.toLowerCase();
      result = result.filter((sy) => sy.code.toLowerCase().includes(search));
    }

    // Sort
    result.sort((a, b) => {
      const aValue = a[sortBy as keyof SchoolYear];
      const bValue = b[sortBy as keyof SchoolYear];

      if (aValue === null || aValue === undefined) return 1;
      if (bValue === null || bValue === undefined) return -1;

      if (typeof aValue === 'string' && typeof bValue === 'string') {
        return sortOrder === 'ASC'
          ? aValue.localeCompare(bValue)
          : bValue.localeCompare(aValue);
      }

      return 0;
    });

    return result;
  }, [schoolYears, searchValue, sortBy, sortOrder]);

  const formatDate = (dateString: string | null) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  };

  const columns: Column<SchoolYear>[] = [
    {
      key: 'code',
      header: 'Code',
      sortable: true,
      render: (schoolYear) => (
        <div className="flex items-center gap-2">
          <span className="font-medium text-gray-900">{schoolYear.code}</span>
          {schoolYear.isCurrent && (
            <Badge variant="success" className="text-xs">
              En cours
            </Badge>
          )}
        </div>
      ),
    },
    {
      key: 'startingDate',
      header: 'Rentree scolaire',
      sortable: true,
      render: (schoolYear) => (
        <span className="text-gray-600">{formatDate(schoolYear.startingDate)}</span>
      ),
    },
    {
      key: 'endingDate',
      header: 'Fin des cours',
      sortable: true,
      render: (schoolYear) => (
        <span className="text-gray-600">{formatDate(schoolYear.endingDate)}</span>
      ),
    },
    {
      key: 'actions',
      header: 'Actions',
      className: 'text-right',
      render: (schoolYear) => (
        <div className="flex items-center justify-end gap-2">
          <Button
            variant="ghost"
            size="icon-sm"
            onClick={() => navigate(`/school-years/${schoolYear.id}/edit`)}
            title="Modifier"
          >
            <Pencil className="h-4 w-4" />
          </Button>
          {isAdmin && (
            <Button
              variant="ghost"
              size="icon-sm"
              onClick={() => setDeleteSchoolYear(schoolYear)}
              title="Supprimer"
              className="text-red-600 hover:text-red-700 hover:bg-red-50"
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          )}
        </div>
      ),
    },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="space-y-2">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10 text-primary">
              <CalendarDays className="h-5 w-5" />
            </div>
            <h1 className="text-2xl md:text-3xl font-bold tracking-tight text-foreground">
              Années fiscales
            </h1>
          </div>
          <p className="text-sm md:text-base text-muted-foreground pl-[52px]">
            Gerez les années fiscales
          </p>
        </div>

        {isAdmin && (
          <Button asChild>
            <Link to="/school-years/new">
              <Plus className="h-4 w-4" />
              Nouvelle annee
            </Link>
          </Button>
        )}
      </div>

      {/* Error message */}
      {error && (
        <div className="rounded-lg bg-red-50 border border-red-200 p-4 text-red-700">
          {error}
        </div>
      )}

      {/* Data Table */}
      <DataTable
        columns={columns}
        data={filteredAndSortedSchoolYears}
        loading={loading}
        searchValue={searchValue}
        onSearchChange={setSearchValue}
        searchPlaceholder="Rechercher par code..."
        sortBy={sortBy}
        sortOrder={sortOrder}
        onSort={handleSort}
        totalItems={filteredAndSortedSchoolYears.length}
        emptyMessage="Aucune année fiscale trouvee"
      />

      {/* Delete confirmation dialog */}
      <AlertDialog open={!!deleteSchoolYear} onOpenChange={() => setDeleteSchoolYear(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Supprimer l'année fiscale ?</AlertDialogTitle>
            <AlertDialogDescription>
              Etes-vous sur de vouloir supprimer l'année fiscale{' '}
              <strong>{deleteSchoolYear?.code}</strong> ?
              <br />
              <br />
              Cette action est irreversible et supprimera egalement l'année fiscale
              de toutes les écoles.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>Annuler</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              disabled={deleting}
              className="bg-red-600 hover:bg-red-700 focus:ring-red-600"
            >
              {deleting ? 'Suppression...' : 'Supprimer'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
